import type { Timestamp } from 'firebase/firestore';
import type { UserRole, QuotedMessage } from './index';
import type { Attachment } from './dm';
import type { ReactionSummary } from './reactions';
import type { EmbeddedContent } from '@/services/community';
import type { MentionData } from './mentions';

/**
 * Leaders Chat Types
 *
 * Type definitions for the encrypted Leaders Chat channel,
 * accessible only to users with 'leader' or 'admin' roles.
 * Mirrors the DM message structure with encryption at rest.
 */

// ==================== Constants ====================

/** Static conversation ID used for HKDF key derivation */
export const LEADERS_CHAT_CHANNEL_ID = 'leaders_chat_channel';

// ==================== Message Types ====================

/**
 * A message in the Leaders Chat channel.
 * Encrypted fields (text, attachments, quotedMessage) are stored as ciphertext.
 * Plaintext metadata fields (isPinned, reactionSummary, etc.) remain readable.
 */
export interface LeadersChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  timestamp: Timestamp | null;
  senderRole: UserRole;
  isDeleted?: boolean;

  // Encrypted fields (same pattern as DM)
  encrypted: boolean;
  ciphertext?: string;
  iv?: string;
  keyVersion?: number;
  hasAttachments?: boolean;

  // Decrypted content (populated at runtime after decryption)
  text: string;
  attachments?: Attachment[];

  // Plaintext metadata (for queries/display without decryption)
  isPinned?: boolean;
  pinnedAt?: Timestamp;
  pinnedBy?: string;
  reactionSummary?: ReactionSummary;

  // Quote/Reply (encrypted in envelope, decrypted at runtime)
  quotedMessageId?: string;
  quotedMessage?: QuotedMessage;

  // Embedded content (stored in encrypted envelope)
  embeddedContent?: EmbeddedContent;

  // Mention data
  /** User IDs of mentioned users (plaintext field for notification targeting) */
  mentionedUserIds?: string[];
  /** Full mention data (decrypted from envelope at runtime) */
  mentions?: MentionData[];

  // Client correlation for optimistic updates
  clientId?: string;
}

// ==================== Channel Metadata ====================

/**
 * Channel-level metadata for the Leaders Chat
 */
export interface LeadersChatConfig {
  createdAt: Timestamp;
  lastMessageAt?: Timestamp;
  lastMessagePreview?: string; // Encrypted preview
  previewIv?: string;
  previewKeyVersion?: number;
}

// ==================== Per-User Metadata ====================

/**
 * Per-user metadata for Leaders Chat.
 * Stored at: artifacts/{appId}/users/{userId}/leaders_chat_metadata/info
 */
export interface LeadersChatUserMetadata {
  unreadCount: number;
  lastReadAt?: Timestamp;
  joinedAt: Timestamp; // Messages before this are filtered out
  isMuted?: boolean;
}

// ==================== Input Types ====================

/**
 * Input for sending a message in Leaders Chat
 */
export interface LeadersChatMessageInput {
  text: string;
  attachments?: Attachment[];
  quotedMessageId?: string;
  quotedMessage?: QuotedMessage;
  embeddedContent?: EmbeddedContent;
  /** Client-generated ID for correlating optimistic updates */
  clientId?: string;
  /** Mention metadata (stored in encrypted envelope) */
  mentions?: MentionData[];
  /** Mentioned user IDs (stored as plaintext for notification targeting) */
  mentionedUserIds?: string[];
}

// ==================== Context Types ====================

/**
 * Leaders Chat context value exposed to components
 */
export interface LeadersChatContextValue {
  /** Decrypted messages (filtered by joinedAt) */
  messages: LeadersChatMessage[];
  /** Total unread count */
  unreadCount: number;
  /** Whether anyone is currently typing */
  typingUsers: { userId: string; userName: string }[];
  /** Whether the context is loading initial data */
  loading: boolean;
  /** Whether the current user has access */
  hasAccess: boolean;

  // Actions
  /** Send a message (encrypts and writes to Firestore) */
  sendMessage: (input: LeadersChatMessageInput) => Promise<string>;
  /** Soft-delete a message */
  deleteMessage: (messageId: string) => Promise<void>;
  /** Pin/unpin a message */
  pinMessage: (messageId: string, isPinned: boolean) => Promise<void>;
  /** Mark all messages as read */
  markAsRead: () => Promise<void>;
  /** Set typing status */
  setTyping: (isTyping: boolean) => void;
}

// ==================== Typing Status ====================

/**
 * Typing status for Leaders Chat
 */
export interface LeadersChatTypingStatus {
  isTyping: boolean;
  userId: string;
  userName: string;
  timestamp: Timestamp;
}
